export const PRODUCT_KEYS = ['PL', 'FL', 'HK', 'OL'] as const;
export type ProductKey = (typeof PRODUCT_KEYS)[number];

export const PRODUCT_LABELS: Record<ProductKey, string> = {
  PL: 'Private Lease',
  FL: 'Financial Lease',
  HK: 'Hire Purchase',
  OL: 'Operational Lease'
};

// Single (broker × product) cell of the mobilistUseForAd flag.
// Only explicit admin overrides are stored; missing entries default to true
// (vehicle is advertised for that combination).
export interface UseForAdSetting {
  broker: string;
  product: ProductKey;
  useForAd: boolean;
}

// Brand and model values for the search dropdowns, as the backend aggregates them from the
// index. Models are nested under their brand because a model name is only unique within one.
export interface VehicleModelOption {
  name: string;
  count: number;
}

export interface VehicleBrandOption {
  name: string;
  count: number;
  models: VehicleModelOption[];
}

// Broker definition fetched from the backend. The frontend uses
// includedDealerLocationIds / excludedDealerLocationIds + the vehicle's
// dealer.locationId to derive which (broker, product) cells are applicable.
export interface Broker {
  name: string;
  products: ProductKey[];
  includedDealerLocationIds?: number[] | null;
  excludedDealerLocationIds?: number[] | null;
}

// The most recent override decision of one kind, as recorded in the backend's audit trail.
// Shown as the badge tooltip; the full timeline lives behind the History action.
export interface MobilistOverrideNote {
  reason?: string | null;
  actor: string;
  at: string;
}

export type MobilistOverrideAction = 'RESERVE' | 'UNRESERVE' | 'AD_BLOCK';

// One entry in a vehicle's override history. Block changes carry only the difference
// from the previous state, not the whole submitted matrix.
export interface MobilistOverrideEvent {
  action: MobilistOverrideAction;
  actor: string;
  at: string;
  reason?: string | null;
  reserved?: boolean | null;
  blocksAdded: Array<{ broker: string; product: ProductKey }>;
  blocksRemoved: Array<{ broker: string; product: ProductKey }>;
}

export interface VehicleListItem {
  id: string;
  // Factory order number: the only identifier a vehicle has before it is registered,
  // i.e. exactly when licensePlate is null.
  komNr: string | null;
  licensePlate: string | null;
  brand: string | null;
  model: string | null;
  type: string | null;
  vehicleType: string | null;
  bodyWork: string | null;
  fuelType: string | null;
  constructionYear: number | null;
  dealerName: string | null;
  dealerLocationId?: number | null;
  // Dealer-driven status (read-only, comes from feed)
  statusNew: boolean | null;
  statusSold: boolean | null;
  statusReserved: boolean | null;
  // Mobilist admin overrides (the portal manages these)
  mobilistStatusReserved: boolean;
  mobilistUseForAd: UseForAdSetting[];
  mobilistReserveNote?: MobilistOverrideNote | null;
  mobilistAdBlockNote?: MobilistOverrideNote | null;
}

export interface VehiclesListResponse {
  count: number | null;
  limit: number | null;
  pageNumber: number | null;
  items: VehicleListItem[];
}

export interface VehicleSearchFilters {
  id?: string;
  komNr?: string;
  licensePlate?: string;
  brand?: string;
  model?: string;
  dealer?: string;
}
